import React, {
  Ref,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { Bubble, Button, Form, Input, notification } from 'tea-component';
// import GPSelectMultiple from '@gp/components/SelectMultiple';
import FollowerRange from '@gp/components/FollowerRange';
import InfoBubble from '@gp/components/InfoBubble';
import OperationBtns from '@gp/components/OperationBtns';
import GPSelect from '@gp/components/Select';
import { FIELD_COMMON_ERR_MSG } from '@gp/constants/const';
import {
  deleteMockUserIdOfIEGG,
  getMockUserIdOfIEGG,
} from '@gp/pages/Pipeline/ProjectDetails/Stakeholder/utils';
import { isLegalHttpUrl } from '@gp/utils/common';
import { useDebounceFn } from 'ahooks';
import classnames from 'classnames';
import { ActionCreators } from 'redux-undo';
import SelectOwner from './components/SelectOwner';
import { CLASSIFICATION_OPT, DEFAULT_ITEM, POSITION_OPT } from './constants';
import './index.scss';
import stakeholdersDataReducer from './reducer';
import { getReqParams } from './utils';
// import CardSlot from '@/pages/globalpedia/components/CardSlot';

interface PropsType {
  isEntryEdit: boolean;
  data: any | null;
  onConfirmDisabledChange: (v: boolean) => void;
}

const BUPLICATE_NAME_ERR_TIPS = 'Duplicate name, please reselect';

const UpdateEditForm = (props: PropsType, ref: Ref<StakeHolderModalForwardProps>) => {
  const { isEntryEdit = false, data = null, onConfirmDisabledChange } = props;

  const initDataRef = useRef<IOwnerList[] | null>(null);
  const notificationDestroysRef = useRef<any[]>([]); // 删除数据时，储存通知弹窗的销毁钩子
  const [stakeholdersData, stakeholdersDataDispatch] = useReducer(stakeholdersDataReducer, {
    past: [],
    present: [],
    future: [],
  });
  const currentOwnerList = stakeholdersData?.present;
  const { run: handleUndoOperation } = useDebounceFn(
    (destroyIndex: number) => {
      stakeholdersDataDispatch(ActionCreators.undo());
      // 销毁对应的通知弹窗
      const destroy = notificationDestroysRef.current?.find(
        (t) => t?.destroyIndex === destroyIndex,
      )?.destroy;
      if (destroy instanceof Function) {
        destroy();
        const newNotificationDestroys = notificationDestroysRef.current?.filter(
          (t) => t?.destroyIndex !== destroyIndex,
        );
        notificationDestroysRef.current = newNotificationDestroys;
      }
    },
    { wait: 300 },
  );

  const isStudioModule = useMemo(() => data?.organization === 'studio', [data?.organization]);

  useImperativeHandle(
    ref,
    () => ({
      getSubmitResult: () => onSubmitResult(initDataRef?.current, currentOwnerList, isStudioModule),
    }),
    [currentOwnerList, isStudioModule],
  );

  const onSubmitResult = useCallback(
    (
      initData: IOwnerList[] | null,
      currentOwnerList: IOwnerList[] | null,
      isStudioModule: boolean,
    ) => {
      // IEGG需要删除前端临时生成的mock的user_id 即重置user_id为空
      const currentOwnerListNew = isStudioModule
        ? currentOwnerList
        : deleteMockUserIdOfIEGG(currentOwnerList);
      // return 'error'; // 告知有错误，弹窗不关闭
      // 整理好参数
      const params = getReqParams(initData, currentOwnerListNew, isStudioModule);

      return params;
    },
    [],
  );

  const reset = useCallback(() => {
    initDataRef.current = null;
    notificationDestroysRef.current = [];
  }, []);

  const handleAdd = useCallback((index: number) => {
    stakeholdersDataDispatch({
      type: 'add',
      payload: { index },
    });
  }, []);

  const handleDelete = useCallback((index: number) => {
    stakeholdersDataDispatch({
      type: 'delete',
      payload: { index },
    });
    const destroyIndex = notificationDestroysRef.current?.length; // 用来做销毁标识
    const { destroy } = notification.success({
      description: (
        <div>
          Successfully delete!&nbsp;
          <Button type="link" onClick={() => handleUndoOperation(destroyIndex)}>
            Undo operation
          </Button>
        </div>
      ),
      // 通知关闭时删除对应的销毁钩子
      onClose: () => {
        const newNotificationDestroys = notificationDestroysRef.current?.filter(
          (t) => t?.destroyIndex !== destroyIndex,
        );
        notificationDestroysRef.current = newNotificationDestroys;
      },
    });
    // 通知弹窗销毁钩子依次push
    notificationDestroysRef.current = [
      ...notificationDestroysRef.current,
      {
        destroyIndex, // 销毁标识
        destroy,
      },
    ];
  }, []);

  const handleUpdate = useCallback(
    (
      index: number,
      item: IOwnerList,
      newItem: {
        [keyName: string]: string | boolean;
      },
    ) => {
      stakeholdersDataDispatch({
        type: 'update',
        payload: {
          index,
          newItemList: {
            ...item,
            ...newItem,
          },
          isStudioModule,
        },
      });
    },
    [isStudioModule],
  );

  // 进入弹窗时默认数据，弹窗消失时重置数据
  useEffect(() => {
    if (isEntryEdit) {
      const list = data?.owner_list;
      const flag = Array.isArray(list) && list?.length;
      const temporiginalData = flag ? list : [DEFAULT_ITEM];
      let initData: IOwnerList[] | null = [];
      // 渲染方式发生改变，所以需要临时处理数据格式，这里把list里的数据放到外层来拉平
      for (let i = 0, l = temporiginalData?.length; i < l; i++) {
        const t = temporiginalData?.[i];
        const renderList =
          Array.isArray(t?.list) && t?.list?.length
            ? t?.list?.map(
                (it: {
                  id?: number;
                  link: string;
                  name: string;
                  position: string;
                  sort?: number;
                  user_id: string;
                }) => ({
                  ...it,
                  project_owner: t?.project_owner,
                  user_id: isStudioModule
                    ? it?.user_id
                    : getMockUserIdOfIEGG(it?.user_id, it?.name),
                }),
              )
            : [
                {
                  project_owner: t?.project_owner,
                  // id: null,
                  // sort: null,
                  link: '',
                  name: '',
                  position: '',
                  user_id: '',
                },
              ];
        initData = [...initData, ...renderList];
      }
      if (initData?.length < 1) initData = null;
      if (flag) {
        if (Array.isArray(initData) && initData?.length) {
          initDataRef.current = initData?.map((t) => ({ ...t }));
        } else {
          initDataRef.current = initData;
        }
      }
      stakeholdersDataDispatch({
        type: 'init',
        payload: {
          data: initData,
        },
      });
    } else {
      reset();
    }
  }, [isEntryEdit, isStudioModule]);

  // confirm按钮是否禁用
  useEffect(() => {
    let result = true;
    if (Array.isArray(currentOwnerList) && currentOwnerList?.length > 0) {
      result = isStudioModule
        ? // owner为输入框
          currentOwnerList?.some(
            (t) => !t?.project_owner || !t?.name || !t?.position || t?.ownerError || t?.linkError,
          )
        : // owner为下拉框
          currentOwnerList?.some(
            (t) => !t?.project_owner || !t?.user_id || !t?.position || t?.ownerError,
          );
    }
    onConfirmDisabledChange?.(result);
  }, [currentOwnerList]);

  return (
    <div className="dbg-update-edit font14">
      <Form className="dbg-update-edit-form">
        {Array.isArray(currentOwnerList) &&
          currentOwnerList?.length > 0 &&
          currentOwnerList?.map((item: IOwnerList, index: number) => {
            const itemLinkError = !!item?.linkError;
            const itemOwnerError = !!item?.ownerError;
            return (
              <Form.Item
                label={`Stakeholder ${index + 1}`}
                key={index}
                className="dbg-update-edit-form-item"
              >
                {/* Select Function */}
                <GPSelect
                  clearable={false}
                  value={item?.project_owner}
                  onChange={(value: string) => {
                    handleUpdate(index, item, {
                      project_owner: value,
                    });
                  }}
                  options={CLASSIFICATION_OPT}
                  placeholder="Please select Function"
                />
                <div className="dbg-update-edit-divider">-</div>

                {/* Select Owner */}
                <InfoBubble
                  className="globalpedia-modal-font14"
                  customBubbleContent={itemOwnerError ? BUPLICATE_NAME_ERR_TIPS : undefined}
                  DomOrText={
                    <SelectOwner
                      defaultValue={{
                        user_id: item?.user_id || '',
                        name: item?.name || '',
                      }}
                      isInput={isStudioModule}
                      isError={itemOwnerError}
                      onChange={(
                        data:
                          | {
                              disabled: boolean;
                              value: string;
                              text: any;
                              data: {
                                user_name: string;
                                user_id: string;
                              };
                            }
                          | string,
                      ) => {
                        if (typeof data === 'string') {
                          // 输入框时
                          handleUpdate(index, item, {
                            name: data,
                          });
                        } else {
                          // 下拉选择框时
                          handleUpdate(index, item, {
                            name: data?.data?.user_name,
                            user_id: data?.value,
                          });
                        }
                      }}
                    />
                  }
                ></InfoBubble>

                <div className="dbg-update-edit-divider">-</div>

                {/* Input Position */}
                <FollowerRange
                  dropdownOverlayBoxClassName="dbg-stakeholder-edit-form_followerrange-select-dropdown-overlay-box"
                  value={item?.position}
                  options={POSITION_OPT}
                  onChange={(value: string) => {
                    handleUpdate(index, item, {
                      position: value,
                    });
                  }}
                  placeholder="Please input Position"
                  customizePlaceholder="Please input Position name"
                />

                {/* Studio-编辑态，Linked In为选填项，可不填 */}
                {isStudioModule && (
                  <Bubble
                    error={itemLinkError}
                    content={itemLinkError ? FIELD_COMMON_ERR_MSG : undefined}
                    trigger="hover"
                  >
                    <Input
                      placeholder="Please input a link of LinkedIn (Optional)"
                      // 这里需要增加个报错的class
                      className={classnames('dbg-update-edit-input', { error: itemLinkError })}
                      value={item?.link}
                      onChange={(value: string) => {
                        const isAllow = isLegalHttpUrl(value) || !value;
                        handleUpdate(index, item, {
                          link: value,
                          linkError: !isAllow,
                        });
                      }}
                    />
                  </Bubble>
                )}

                {/* 操作按钮 */}
                <OperationBtns
                  className="dbg-update-edit-operate"
                  isShowDelete={currentOwnerList?.length > 1}
                  isShowAdd={index === currentOwnerList?.length - 1}
                  onAdd={() => handleAdd(index)}
                  onDelete={() => handleDelete(index)}
                />
              </Form.Item>
            );
          })}
      </Form>
    </div>
  );
};

export default memo(forwardRef(UpdateEditForm));
